import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert, Box, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { downloadBlob } from '../../../lib/download';
import { trackerBoardKey } from '../../../lib/groupQueryKeys';
import { guestExportRows } from '../../../lib/hostExport/guestExport';
import {
  fetchTrackerBoard, fetchTrackerExport, trackerFailureMessage,
} from '../../../services/tracker-api-client';
import type { GroupDetail } from '../../../types/groups';
import PrintableSection from './PrintableSection';
import QueryState from './QueryState';

/**
 * Name, RSVP status, plus-one, email, phone — the guest list a host can act
 * on standing at check-in.
 *
 * The printed table comes from `guestExportRows` (lib/hostExport/guestExport.ts),
 * which resolves status through `guestStatus` exactly as the Guests tab does;
 * this component does not touch `rsvpStatus` itself.
 *
 * The CSV is the SERVER's guest export — the same file the Guests tab's own
 * Export button downloads. It resolves status the same way and carries
 * arrival, departure, room and notes that the printed view leaves out. A
 * second, client-built guest CSV under the same filename would be two files
 * whose columns drift apart.
 *
 * The board is read under the same query key `useBroadcastRoster` uses, and
 * stored in the same shape (the raw ok/failure result), so the two tabs
 * share one cache entry rather than each paying for a fetch.
 */
export default function GuestExportSection({
  group,
  printing,
  onPrint,
}: {
  group: GroupDetail;
  printing: boolean;
  onPrint: () => void;
}) {
  const boardQuery = useQuery(trackerBoardKey(group.id), () => fetchTrackerBoard(group.id));
  const board = boardQuery.data?.ok ? boardQuery.data.data : null;
  const loadError = boardQuery.data && !boardQuery.data.ok
    ? trackerFailureMessage(boardQuery.data.failure)
    : boardQuery.error
      ? "Couldn't load the guest list."
      : '';

  const rows = useMemo(() => guestExportRows(board?.guests ?? []), [board]);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const downloadCsv = async () => {
    setExporting(true);
    setExportError('');
    const result = await fetchTrackerExport(group.id);
    setExporting(false);
    if (!result.ok) {
      setExportError(trackerFailureMessage(result.failure));
      return;
    }
    downloadBlob(`guest-list-${group.slug}.csv`, result.data);
  };

  const ready = Boolean(board);

  return (
    <PrintableSection
      title="Guest list"
      description="Name, RSVP status, plus-one, email, phone — everyone on the roster."
      printing={printing}
      onPrint={ready ? onPrint : undefined}
      onDownloadCsv={ready && !exporting ? downloadCsv : undefined}
      downloadLabel={exporting ? 'Preparing…' : 'Download CSV'}
    >
      <QueryState loading={boardQuery.isLoading} loadingLabel="Loading guests…" error={loadError} />
      {exportError ? (
        <Alert severity="error" sx={{ mb: 2, '@media print': { display: 'none' } }}>{exportError}</Alert>
      ) : null}
      {ready ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">Name</TableCell>
                <TableCell component="th" scope="col">RSVP status</TableCell>
                <TableCell component="th" scope="col">Plus-one of</TableCell>
                <TableCell component="th" scope="col">Email</TableCell>
                <TableCell component="th" scope="col">Phone</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                // eslint-disable-next-line react/no-array-index-key -- rows have no stable id of their own
                <TableRow key={index}>
                  <TableCell component="th" scope="row">{row.name}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.plusOneOf}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.phone}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1, '@media print': { display: 'none' } }}
          >
            {rows.length} guest{rows.length === 1 ? '' : 's'}
          </Typography>
        </Box>
      ) : null}
    </PrintableSection>
  );
}
